import { ALERT_COURIER_PHASES, mapRawCourierStatusToPhase, orderCourierLocation } from '@order-up/models';
import { Check, Clock, MapPin, Navigation, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { resolveProgress, type TrackableOrder } from './orderStatusProgress';

/**
 * Live order-status tracker for the confirmation page. Renders a progress
 * timeline driven by the REAL order signals — the kitchen lifecycle
 * (kitchenState: pending → sent → preparing → ready → served) for pickup and
 * the courier phases for delivery — so the customer sees DoorDash/UberEats-style
 * marketable statuses ("Being prepared", "Ready for pickup", "Out for delivery").
 *
 * For delivery it also surfaces the courier name, ETA, a hosted tracking link,
 * and a live driver-location map with the distance to the customer (best-effort
 * geocode of the dropoff) and a freshness stamp.
 *
 * The parent polls the order (useGetOrderByGuid refetchInterval) so this view
 * advances — and the map re-centers — as the order progresses.
 */

interface OrderStatusTrackerProps {
    order: TrackableOrder;
    brandColor: string;
}

const TERMINAL_CANCELLED = ['cancelled', 'voided'];

/** Great-circle distance between two points, in miles. */
function haversineMiles(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
    const toRad = (d: number) => (d * Math.PI) / 180;
    const R = 3958.8; // Earth radius (miles)
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** "just now" / "3m ago" / "2h ago" from an ISO timestamp. */
function relativeTime(iso: string | undefined, now: number): string | null {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (isNaN(t)) return null;
    const secs = Math.max(0, Math.round((now - t) / 1000));
    if (secs < 45) return 'just now';
    if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
    return `${Math.round(secs / 3600)}h ago`;
}

/** Format an ISO ETA as a local time, falling back to the raw string. */
function formatEta(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export default function OrderStatusTracker({ order, brandColor }: OrderStatusTrackerProps) {
    const isDelivery = order.type === 'delivery';
    const cancelled =
        TERMINAL_CANCELLED.includes(order.orderState ?? '') ||
        // Through the ONE raw→phase map, so every provider's spelling (Uber's
        // `canceled`, Drive's `DELIVERY_CANCELLED`, a `returned` courier) counts.
        ALERT_COURIER_PHASES.has(mapRawCourierStatusToPhase(order.courierStatus));

    // Best-effort geocode of the dropoff address so we can show the driver's
    // distance to the customer. Free OSM Nominatim; degrades silently on failure.
    const dropoffQuery = useMemo(() => {
        const a = order.deliveryAddressComponents;
        if (!isDelivery || !a?.line1) return '';
        return [a.line1, a.city, a.state, a.postalCode].filter(Boolean).join(', ');
    }, [isDelivery, order.deliveryAddressComponents]);

    const [dropoff, setDropoff] = useState<{ lat: number; lng: number } | null>(null);
    useEffect(() => {
        if (!dropoffQuery) return;
        let cancelledFetch = false;
        fetch( // wire-fold-ok: vendor API (OpenStreetMap Nominatim)
            `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(dropoffQuery)}`,
            { headers: { Accept: 'application/json' } },
        )
            .then((r) => (r.ok ? r.json() : []))
            .then((res: Array<{ lat: string; lon: string }>) => {
                if (cancelledFetch || !Array.isArray(res) || !res[0]) return;
                const lat = parseFloat(res[0].lat);
                const lng = parseFloat(res[0].lon);
                if (!isNaN(lat) && !isNaN(lng)) setDropoff({ lat, lng });
            })
            .catch(() => {
                /* distance is best-effort; ignore */
            });
        return () => {
            cancelledFetch = true;
        };
    }, [dropoffQuery]);

    // A ticking clock so the "Updated Xm ago" freshness stamp stays current
    // between order polls without calling Date.now() during render.
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(id);
    }, []);

    if (cancelled) {
        return (
            <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                        <X className="w-5 h-5 text-red-600" strokeWidth={3} />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">
                            {order.orderState === 'voided' ? 'Order voided' : 'Order cancelled'}
                        </h3>
                        <p className="text-sm text-gray-600">
                            If you believe this is a mistake, please contact the restaurant.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    const { steps, activeIndex, activeSubtitle } = resolveProgress(order);
    const driver = orderCourierLocation(order);
    const distanceMi = driver && dropoff ? haversineMiles(driver, dropoff) : null;
    const freshness = relativeTime(order.courierStatusUpdatedAt, now);

    // Map bounding box covers the driver (+ dropoff when known) with padding.
    const mapSrc = (() => {
        if (!driver) return '';
        const pts = dropoff ? [driver, dropoff] : [driver];
        const pad = 0.008;
        const minLat = Math.min(...pts.map((p) => p.lat)) - pad;
        const maxLat = Math.max(...pts.map((p) => p.lat)) + pad;
        const minLng = Math.min(...pts.map((p) => p.lng)) - pad;
        const maxLng = Math.max(...pts.map((p) => p.lng)) + pad;
        return `https://www.openstreetmap.org/export/embed.html?bbox=${minLng}%2C${minLat}%2C${maxLng}%2C${maxLat}&layer=mapnik&marker=${driver.lat}%2C${driver.lng}`;
    })();

    return (
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-6">
            <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-bold text-gray-900">Order status</h3>
                {order.eta && (
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700">
                        <Clock className="w-3.5 h-3.5" /> ETA {formatEta(order.eta)}
                    </span>
                )}
            </div>

            <ol className="relative">
                {steps.map((label, i) => {
                    const done = i < activeIndex;
                    const active = i === activeIndex;
                    const isLast = i === steps.length - 1;
                    return (
                        <li key={label} className="flex gap-3 pb-5 last:pb-0">
                            <div className="flex flex-col items-center">
                                <div
                                    className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                                    style={{
                                        backgroundColor: done || active ? brandColor : '#e5e7eb',
                                        color: done || active ? '#fff' : '#9ca3af',
                                    }}
                                >
                                    {done ? (
                                        <Check className="w-4 h-4" strokeWidth={3} />
                                    ) : (
                                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: active ? '#fff' : '#9ca3af' }} />
                                    )}
                                </div>
                                {!isLast && (
                                    <div
                                        className="w-0.5 flex-1 mt-1"
                                        style={{ backgroundColor: done ? brandColor : '#e5e7eb', minHeight: 20 }}
                                    />
                                )}
                            </div>
                            <div className="pt-0.5">
                                <p className={`text-sm font-medium ${done || active ? 'text-gray-900' : 'text-gray-400'}`}>
                                    {label}
                                </p>
                                {active && activeSubtitle && (
                                    <p className="text-xs text-gray-500 mt-0.5">{activeSubtitle}</p>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ol>

            {isDelivery && (order.trackingUrl || order.courierName || driver) && (
                <div className="mt-5 pt-5 border-t border-gray-200 space-y-3">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        {order.courierName && (
                            <p className="text-sm text-gray-700">
                                <span className="text-gray-500">Courier:</span>{' '}
                                <span className="font-medium">{order.courierName}</span>
                            </p>
                        )}
                        {distanceMi != null && (
                            <span
                                className="inline-flex items-center gap-1.5 text-sm font-semibold px-2.5 py-1 rounded-full"
                                style={{ backgroundColor: `${brandColor}1a`, color: brandColor }}
                            >
                                <Navigation className="w-3.5 h-3.5" />
                                {distanceMi < 0.1 ? 'Arriving now' : `${distanceMi.toFixed(1)} mi away`}
                            </span>
                        )}
                    </div>

                    {driver && (
                        <div className="rounded-lg overflow-hidden border border-gray-200">
                            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 text-xs text-gray-600">
                                <span className="inline-flex items-center gap-1.5">
                                    <MapPin className="w-3.5 h-3.5" /> Driver location
                                </span>
                                {freshness && <span>Updated {freshness}</span>}
                            </div>
                            <iframe
                                // Re-keys on each new driver ping so the map re-centers live.
                                key={`${driver.lat},${driver.lng}`}
                                title="Driver location"
                                className="w-full h-48"
                                loading="lazy"
                                src={mapSrc}
                            />
                        </div>
                    )}

                    {order.trackingUrl && (
                        <a
                            href={order.trackingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-white text-sm w-full sm:w-auto justify-center"
                            style={{ backgroundColor: brandColor }}
                        >
                            <Navigation className="w-4 h-4" />
                            Track on the courier’s live map
                        </a>
                    )}
                </div>
            )}
        </div>
    );
}
